import { getElementIdFromHash } from '@pm2/common-utils';
import { iframeComponentJsxPrinters } from '@pm2/iframe-component';
import { imageComponentJsxPrinters } from '@pm2/image-component';
import { mediaComponentJsxPrinters } from '@pm2/media-component';
import { textNodeToJsxPrinter, htmlNodeToJsxPrinter, generateJsxKey, convertHtmlAttributesToReactProps, allowedAttributeMap } from '@utils/ast';
import { isPlatformNodeJS } from '@utils/common';
import { GlossaryWord } from './atoms/glossary-word.js';
import { TextLink } from './atoms/link.js';
import { RichTextMedia } from './children/richtext-media.js';
import { Table } from './children/table.js';
import { TextComponent } from './text-component.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { createElement } from 'react';

const printTextComponent = (node, context) => {
  /**
   * detect the index of the accordion to which should be expanded due to
   * hash changes (if any)
   */
  const onHashChanged = () => {
    return shouldExpandNodeInitially(node);
  };
  return /*#__PURE__*/jsxs(TextComponent, {
    layout: node.options.layout,
    ribbonId: node.pm2.ribbonIconId,
    autoCenter: node.options.autoCenter,
    collapsible: node.options.isCollapsible,
    scrambled: node.pm2.isScrambled,
    displayCaption: node.options.displayCaption,
    isRichtext: node.meta.isRichText,
    caption: node.meta.caption,
    attachedAudioFile: node.pm2.speechFileUrl,
    initiallyExpanded: shouldExpandNodeInitially(node),
    onHashChange: onHashChanged,
    children: [node.slots?.contentBetweenCaptionAndContent && /*#__PURE__*/jsx(TextComponent.BetweenCaptionAndContent, {
      children: node.slots.contentBetweenCaptionAndContent
    }), context.printChildren(node.children, {
      ...textComponentChildPrinters,
      ...context.parentPrinters
    })]
  }, generateJsxKey(context));
};
const printTextComponentHtml = (node, context) => {
  // sastisfy TypeScript with a generic assumption that every node we print
  // will allow children like a div-tag
  const TagName = node.tagName;
  const attributeMap = allowedAttributeMap[node.tagName];
  if (!attributeMap) {
    // don't render html tags that hasn't been explicitly configured inside
    // the attribute map
    return null;
  }
  const props = node.attributes ? convertHtmlAttributesToReactProps(node.tagName, node.attributes, attributeMap) : {};
  const children = node.children !== undefined ? printHtmlChildren(node, context, {
    ...iframeComponentJsxPrinters,
    ...imageComponentJsxPrinters,
    ...mediaComponentJsxPrinters,
    ...textComponentJsxPrinters,
    html: htmlNodeToJsxPrinter,
    text: textNodeToJsxPrinter,
    "text-component.html": printTextComponentHtml,
    "text-component.media": printMediaContainer,
    "text-component.table": printTable,
    "text-component.anchor": printAnchor,
    "text-component.glossary-word": printGlossaryWord,
    ...context.parentPrinters
  }) : undefined;
  return /*#__PURE__*/jsx(TagName, {
    ...props,
    children: children
  }, generateJsxKey(context));
};
const printTable = (node, context) => {
  return /*#__PURE__*/jsx(Table, {
    bordered: node.options.enableBorderedLayout,
    striped: node.options.enableStripedLayout,
    hover: node.options.enableHoverLayout,
    alternative: node.options.enableAlternativeLayout,
    responsive: true,
    children: context.printChildren(node.children, {
      ...iframeComponentJsxPrinters,
      ...imageComponentJsxPrinters,
      ...mediaComponentJsxPrinters,
      ...textComponentJsxPrinters,
      "text-component.html": printTextComponentHtml
    })
  }, generateJsxKey(context));
};
const printMediaContainer = (node, context) => {
  const media = context.printChildren(node.content.media, {
    ...iframeComponentJsxPrinters,
    ...imageComponentJsxPrinters,
    ...mediaComponentJsxPrinters
  });
  const children = context.printChildren(node.content.children, {
    ...textComponentChildPrinters,
    ...context.parentPrinters
  });
  return /*#__PURE__*/jsx(RichTextMedia, {
    align: node.options.align,
    children: {
      media,
      children
    }
  }, generateJsxKey(context));
};
const printAnchor = (node, context) => {
  if (!node.children) {
    return null;
  }
  const props = node.attributes ? convertHtmlAttributesToReactProps("a", node.attributes, allowedAttributeMap["a"] ?? {}) : {};
  return /*#__PURE__*/createElement(TextLink, {
    ...props,
    key: generateJsxKey(context),
    theme: node.meta.theme,
    target: node.meta.target,
    href: node.href
  }, context.printChildren(node.children, {
    html: htmlNodeToJsxPrinter,
    text: textNodeToJsxPrinter
  }));
};
const printGlossaryWord = (node, context) => {
  if (!node.children) {
    return null;
  }
  return /*#__PURE__*/jsx(GlossaryWord, {
    id: node.meta.glossaryWordId,
    children: context.printChildren(node.children, {
      html: htmlNodeToJsxPrinter,
      text: textNodeToJsxPrinter
    })
  }, generateJsxKey(context));
};
const textComponentJsxPrinters = {
  "text-component": printTextComponent
};
const textComponentChildPrinters = {
  ...iframeComponentJsxPrinters,
  ...imageComponentJsxPrinters,
  ...mediaComponentJsxPrinters,
  ...textComponentJsxPrinters,
  html: htmlNodeToJsxPrinter,
  text: textNodeToJsxPrinter,
  "text-component.html": printTextComponentHtml,
  "text-component.media": printMediaContainer,
  "text-component.table": printTable,
  "text-component.anchor": printAnchor,
  "text-component.glossary-word": printGlossaryWord
};

/**
 * print html children, taking special cases into consideration to avoid having
 * runtime exceptions / warnings from React
 */
function printHtmlChildren(node, context, printers) {
  if (!node.children) {
    return;
  }
  switch (node.tagName.toLowerCase()) {
    case "area":
    case "base":
    case "br":
    case "col":
    case "embed":
    case "hr":
    case "img":
    case "input":
    case "keygen":
    case "link":
    case "menuitem":
    case "meta":
    case "param":
    case "source":
    case "track":
    case "wbr":
      // the following tags simply don't allow children at all, so avoid even
      // attempting to parse them
      return undefined;
    case "table":
    case "thead":
    case "tbody":
    case "tfoot":
    case "tr":
      // tables don't allow whitespace or text being embedded directly inside
      // any tags except for td and th, so avoid rendering text nodes here
      return context.printChildren(node.children?.filter(it => it.nodeType !== "text"), printers);
    default:
      // ... otherwise print children as we usually would
      return context.printChildren(node.children, printers);
  }
}
function shouldExpandNodeInitially(node) {
  const learningObjectId = (() => {
    if (isPlatformNodeJS()) {
      return undefined;
    }
    return getElementIdFromHash(window.location.hash);
  })();
  return !!learningObjectId && node.id === learningObjectId;
}

export { printTextComponent, textComponentChildPrinters, textComponentJsxPrinters };
//# sourceMappingURL=printer.to-jsx.js.map
